
































































import { computed, defineComponent, PropType, ref } from "@vue/composition-api";
import { differenceInDays } from "date-fns";
import { FundraisingGoal, FundraisingGoalStatus } from "@scrile/api-provider/dist/api/FundraisingGoalsProvider";
import providers from "@/lib/api";
import { $t } from "@/lib/i18nHelpers";
import { formatDate } from "@/lib/time";
import { QuickTip } from "@/types";
import AvatarsGroup from "@/components/AvatarsGroup";
import BaseButton from "@/components/BaseButton";
import BaseIcon from "@/components/BaseIcon";
import FormatMoney from "@/components/FormatMoney";
import QuickTipsItem from "@/components/QuickTipsItem";
import FinishGoalConfirm from "./components/FinishGoalConfirm.vue";
import GoalTipConfirm from "./components/GoalTipConfirm.vue";

export default defineComponent({
  name: "FundraisingGoalCard",
  components: {
    AvatarsGroup,
    GoalTipConfirm,
    FinishGoalConfirm,
    QuickTipsItem,
    BaseIcon,
    BaseButton,
    FormatMoney,
  },
  props: {
    goal: { type: Object as PropType<FundraisingGoal>, required: true },
    showButtons: { type: Boolean },
    isAuthor: { type: Boolean, required: true },
    canUpdateGoal: { type: Boolean, required: true },
  },
  setup(props, { root, emit }) {
    const showFinishConfirm = ref(false);
    const showTipConfirm = ref(false);
    const loading = ref(false);

    const goalDateText = computed(() => {
      if (props.goal.finishedTime) {
        return $t("Ended {0}", [
          formatDate(new Date(props.goal.finishedTime), {
            year: "numeric",
            month: "2-digit",
            day: "2-digit",
            hour: "2-digit",
            minute: "2-digit",
          }),
        ]);
      }
      const difference = differenceInDays(new Date(props.goal.dueTime), new Date());
      return $t("{0} days remaining", [difference > 0 ? difference : 0]);
    });

    const progress = computed(() => {
      return `${Math.round((props.goal.currentAmount / props.goal.goalAmount) * 100)}%`;
    });

    const isActive = computed(() => props.goal.status === FundraisingGoalStatus.ACTIVE);
    const isDeclined = computed(() => props.goal.status === FundraisingGoalStatus.DECLINED);
    const isUnderVerification = computed(() => props.goal.status === FundraisingGoalStatus.NEW);

    const tipItem = computed<QuickTip | null>(() => {
      if (isUnderVerification.value)
        return { icon: "sci-progress-check", type: "info", text: $t("This goal is being verified") };
      if (isDeclined.value)
        return {
          icon: "sci-information-triangle",
          type: "warning",
          text: props.goal.declineReason || $t("Decline reason was not provided"),
        };
      return null;
    });

    const onShowDetails = () => {
      root.$router.push({ name: "goalsById", params: { goalId: props.goal.id } });
    };

    const onFinishGoal = async () => {
      if (!props.canUpdateGoal) return;
      try {
        loading.value = true;
        const response = await providers.FundraisingGoalsProvider.finish({ id: props.goal.id });
        emit("goalFinished", response);

        showFinishConfirm.value = false;
      } finally {
        loading.value = false;
      }
    };

    return {
      loading,
      showFinishConfirm,
      showTipConfirm,
      goalDateText,
      progress,
      isActive,
      tipItem,
      onFinishGoal,
      onShowDetails,
    };
  },
});
